/**
 * session-storage-guard — the privacy boundary for per-user client storage.
 *
 * The disease this cures: web storage outlives the signed-in identity. Every
 * per-user Zustand store hydrates from fixed keys (`bash:tasks` in
 * sessionStorage, `bash:taskId` / `vestra-computer-state-v2` / … in
 * localStorage) at MODULE-INIT, and nothing wiped them on sign-out. So the next
 * account on this browser saw the previous user's tasks/messages flash before
 * the invite/onboarding gate resolved — a privacy leak.
 *
 * The fix wipes our app's web storage at the identity boundary, in one place
 * that knows nothing about individual stores:
 *
 *   - clearUserScopedStorage() — called on sign-out (AuthContext.signOut is the
 *     single seam every sign-out path funnels through). Removes app keys and
 *     leaves storage clean *before* the next user's session can exist, so the
 *     reported sign-out → switch-account path has no stale frame at all.
 *
 *   - reconcileStorageOwner(uid) — called whenever an authenticated user
 *     resolves. Covers the silent switch (a different account signs in with no
 *     explicit sign-out): if the on-disk state is owned by a different user, it
 *     wipes and hard-reloads so module-init re-hydrates from an empty slate.
 *
 * Why an allowlist-to-PRESERVE and not a denylist-to-delete: forgetting to
 * preserve a key just logs the user out (loud, caught immediately in testing);
 * forgetting to delete a key silently leaks data — the exact bug being killed.
 * The safe default must be "wipe".
 */

/** localStorage key recording which user owns the current on-disk app state. */
const OWNER_KEY = "bash:lastUserId";

// Keys that must survive an identity-boundary wipe:
//  - `sb-*`      Supabase auth. Cookie-based today (createBrowserClient from
//                @supabase/ssr), but preserved defensively so that if the
//                session ever lands in localStorage we never sign the user out
//                mid-boot.
//  - bash-theme  device/display preference, not user data — wiping it would
//                strobe the theme on every normal sign-out → sign-in.
// The OWNER_KEY itself is intentionally NOT preserved here; callers re-stamp it
// explicitly after a wipe.
const PRESERVE_KEYS = new Set<string>(["bash-theme"]);
const PRESERVE_PREFIXES = ["sb-"];

function shouldPreserve(key: string): boolean {
  if (PRESERVE_KEYS.has(key)) return true;
  return PRESERVE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/** Remove every non-preserved key from a Storage. Snapshots keys first so the
 *  live index never shifts under removeItem mid-iteration. */
function wipe(store: Storage): void {
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key !== null && !shouldPreserve(key)) doomed.push(key);
  }
  for (const key of doomed) store.removeItem(key);
}

/**
 * Wipe all per-user app state from web storage, preserving only the auth + theme
 * allowlist. Safe to call on every sign-out; a no-op on the server.
 */
export function clearUserScopedStorage(): void {
  if (typeof window === "undefined") return;
  try {
    wipe(window.localStorage);
  } catch {
    /* storage unavailable (private mode / disabled) — nothing to leak */
  }
  try {
    wipe(window.sessionStorage);
  } catch {
    /* idem */
  }
}

/**
 * Ensure the on-disk per-user state belongs to `userId`. Returns whether a
 * reload was triggered (when true the caller need do nothing — the page is
 * already reloading).
 *
 *   - marker absent   → claim ownership, no wipe. A fresh browser, or storage a
 *                       prior sign-out already cleared: this user's own
 *                       optimistic state must survive, and there is nothing to
 *                       leak.
 *   - marker === uid  → no-op (same user: normal load or token refresh).
 *   - marker !== uid  → a different user owns this browser's state (silent
 *                       account switch): wipe it and hard-reload so every
 *                       module-init store re-hydrates empty.
 *
 * `reload` is injectable so tests can assert the reload without a real
 * navigation (jsdom has none).
 */
export function reconcileStorageOwner(
  userId: string,
  reload: () => void = () => window.location.reload()
): boolean {
  if (typeof window === "undefined" || !userId) return false;

  let marker: string | null = null;
  try {
    marker = window.localStorage.getItem(OWNER_KEY);
  } catch {
    return false; // storage unreadable — can't reason about ownership
  }

  if (marker === userId) return false;

  if (marker === null) {
    // No prior owner — claim it without disturbing this user's own state.
    try {
      window.localStorage.setItem(OWNER_KEY, userId);
    } catch {
      /* best effort */
    }
    return false;
  }

  // A different user owns this browser's on-disk state — purge it.
  clearUserScopedStorage();

  // Only reload once the new ownership is durably written; otherwise a failing
  // write would re-detect the mismatch on the next load and reload-loop forever.
  let claimed = false;
  try {
    window.localStorage.setItem(OWNER_KEY, userId);
    claimed = window.localStorage.getItem(OWNER_KEY) === userId;
  } catch {
    /* storage disabled — fall through without reloading */
  }

  if (claimed) {
    reload();
    return true;
  }
  return false;
}
